import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type {
  AverageScoreTeamMode,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { AddToWatchlistButton } from '#src/components/watchlists/AddToWatchlistButton/index.tsx';
import { RemoveFromWatchlistButton } from '#src/components/watchlists/RemoveFromWatchlistButton/index.tsx';
import { useTeamMode } from '#src/searchCommonPlayer/teamMode.ts';
import { LazyFixtureChart } from '#src/shared/stats/LastScores/LazyFixtureChart/index.tsx';

import { BuyingFloorPricePlayerDetails } from './BuyingFloorPricePlayerDetails/index.tsx';
import { BuyingPlayerDetailsButton } from './BuyingPlayerDetailsButton/index.tsx';
import { PlayerInjuryReport } from './PlayerInjuryReport/index.tsx';
import { PlayerNewsUpdates } from './PlayerNewsUpdates/index.tsx';
import { PlayerProperties } from './PlayerProperties/index.tsx';
import { PlayerStats } from './PlayerStats/index.tsx';
import { PlayerUnavailabilityPanel } from './PlayerUnavailabilityPanel/index.tsx';
import { PlayerUpcomingGames } from './PlayerUpcomingGames/index.tsx';
import type {
  PlayerDetailsQuery,
  PlayerDetailsQueryVariables,
  PlayerDetails_playerGameScoreInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  min-height: 100%;
`;
const Main = styled(FullWidthContainer)`
  padding-top: var(--container-padding);
  padding-bottom: var(--container-padding);
  flex-grow: 1;
`;
const Header = styled(SBHorizontal)`
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--c-black);
  padding: var(--double-unit) var(--double-unit) var(--double-unit) var(--unit);
  border-bottom: 1px solid var(--c-nd-150);
`;
const Content = styled(Vertical)`
  > *:not(:last-child) {
    padding-bottom: var(--triple-unit);
    border-bottom: 1px solid var(--c-nd-150);
  }
`;
const Footer = styled(Vertical).attrs({ gap: 2 })`
  ${regularMaterial}
  position: sticky;
  bottom: 0;
  padding: var(--double-unit);
  border-top: 1px solid var(--c-nd-150);
`;

const PLAYER_DETAILS_QUERY = gql`
  query PlayerDetailsQuery(
    $playerSlug: String!
    $cardSlug: String!
    $gameId: ID
    $so5LeaderboardSlug: String
    $shouldFetchCard: Boolean!
    $inSeason: Boolean
    $rarity: Rarity
    $withBuyingOptions: Boolean!
    $onlyPrimary: Boolean = false
    $teamMode: AverageScoreTeamMode
  ) {
    anyPlayer(slug: $playerSlug) {
      slug
      activeClub {
        slug
        ...PlayerUpcomingGames_teamInterface
        upcomingAnyGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      activeNationalTeam {
        slug
        ...PlayerUpcomingGames_teamInterface
        upcomingAnyGames(first: 3) {
          id
          ...PlayerUpcomingGames_game
        }
      }
      news(last: 10) {
        date
      }
      scopedSeasonAverage: averageScore(
        type: SEASON_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastFiveSo5Average: averageScore(
        type: LAST_FIVE_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastTenPlayedSo5Average: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastFortySo5Average: averageScore(
        type: LAST_FORTY_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      ...PlayerProperties_anyPlayer
      ...PlayerUnavailabilityPanel_anyPlayer
      ...PlayerInjuryReport_anyPlayer
      ...PlayerNewsUpdates_anyPlayer
      ...BuyingPlayerDetailsButton_anyPlayer
      ...BuyingFloorPricePlayerDetails_anyPlayer
      ...RemoveFromWatchlistButton_anyPlayer
      ...AddToWatchlistButton_anyPlayer
      ...PlayerStats_anyPlayerInterface
    }
    anyCard(slug: $cardSlug) @include(if: $shouldFetchCard) {
      slug
      anyPositions
      eligiblePlayerGameScores(gameId: $gameId) {
        id
        ...PlayerStats_playerGameScoreInterface
      }
      ...PlayerProperties_anyCard
      ...PlayerStats_anyCardInterface
    }
  }
  ${PlayerUpcomingGames.fragments.game}
  ${PlayerUpcomingGames.fragments.team}
  ${PlayerProperties.fragments.anyPlayer}
  ${PlayerProperties.fragments.anyCard}
  ${PlayerUnavailabilityPanel.fragments.anyPlayer}
  ${PlayerInjuryReport.fragments.anyPlayer}
  ${PlayerNewsUpdates.fragments.anyPlayer}
  ${BuyingPlayerDetailsButton.fragments.anyPlayer}
  ${BuyingFloorPricePlayerDetails.fragments.anyPlayer}
  ${RemoveFromWatchlistButton.fragments.anyPlayer}
  ${AddToWatchlistButton.fragments.anyPlayer}
  ${PlayerStats.fragments.anyPlayerInterface}
  ${PlayerStats.fragments.anyCardInterface}
  ${PlayerStats.fragments.playerGameScoreInterface}
` as TypedDocumentNode<PlayerDetailsQuery, PlayerDetailsQueryVariables>;

type Props = {
  playerSlug: string;
  cardSlug?: string;
  gameId?: string;
  onClose?: () => void;
  extraActions?: ReactNode;
  so5LeaderboardSlug?: string;
  onScoreSelect?: ComponentProps<typeof LazyFixtureChart>['onScoreSelect'];
  withBuyingOptions?: {
    rarity: Rarity;
    inSeason: boolean;
    onMarketClick: (playerSlug: string) => void;
  };
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  onboarding?: boolean;
  footer?: ReactNode;
  teamMode?: AverageScoreTeamMode;
};

export const PlayerDetails = ({
  playerSlug,
  cardSlug,
  gameId,
  so5LeaderboardSlug,
  onClose,
  extraActions,
  onScoreSelect,
  withBuyingOptions,
  watchlistId,
  favoritesWatchlist,
  onboarding,
  footer,
  teamMode: teamModeProp,
}: Props) => {
  const { formatMessage } = useIntl();
  const teamModeFromUrl = useTeamMode();
  const teamMode = teamModeProp ?? teamModeFromUrl;

  const { data: playerDetailsData, loading: playerDetailsLoading } = useQuery(
    PLAYER_DETAILS_QUERY,
    {
      variables: {
        playerSlug,
        gameId,
        so5LeaderboardSlug,
        cardSlug: cardSlug || '',
        shouldFetchCard: !!cardSlug,
        inSeason: withBuyingOptions?.inSeason,
        rarity: withBuyingOptions?.rarity,
        withBuyingOptions: !!withBuyingOptions,
        teamMode,
      },
    }
  );

  const { anyCard } = playerDetailsData || {};
  if (playerDetailsLoading || !playerDetailsData) {
    return (
      <Root>
        <LoadingIndicator fullHeight />
      </Root>
    );
  }

  const { activeClub, activeNationalTeam } = playerDetailsData?.anyPlayer || {};
  const teamsForUpcomingGames =
    teamMode === 'CLUB'
      ? [activeClub]
      : teamMode === 'NATIONAL_TEAM'
        ? [activeNationalTeam]
        : [activeClub, activeNationalTeam];
  const upcomingGamesWithTeam = teamsForUpcomingGames
    .filter(Boolean)
    .flatMap(team =>
      team.upcomingAnyGames.filter(Boolean).map(game => ({ game, team }))
    );

  return (
    <Root>
      {!!onClose && (
        <Header>
          <SBHorizontal>
            <IconButton
              size="medium"
              onClick={onClose}
              icon={iconChevronLeft}
              color="transparent"
              aria-label={formatMessage(glossary.close)}
            />
            {!!extraActions && <Horizontal>{extraActions}</Horizontal>}
          </SBHorizontal>
        </Header>
      )}
      <Main>
        <Content gap={3}>
          <Vertical gap={2}>
            <PlayerProperties
              usePlayerPicture
              useCardPositions
              card={anyCard}
              player={playerDetailsData.anyPlayer}
              disableLinks={onboarding}
            />
            <PlayerStats
              anyPlayer={playerDetailsData.anyPlayer}
              anyCard={anyCard}
              eligiblePlayerGameScores={
                playerDetailsData.anyCard?.eligiblePlayerGameScores || []
              }
              scopedScores={
                teamMode && teamMode !== 'ALL'
                  ? {
                      SEASON_AVERAGE_SCORE:
                        playerDetailsData.anyPlayer.scopedSeasonAverage,
                      LAST_FIVE_SO5_AVERAGE_SCORE:
                        playerDetailsData.anyPlayer.scopedLastFiveSo5Average,
                      LAST_TEN_PLAYED_SO5_AVERAGE_SCORE:
                        playerDetailsData.anyPlayer
                          .scopedLastTenPlayedSo5Average,
                      LAST_FORTY_SO5_AVERAGE_SCORE:
                        playerDetailsData.anyPlayer.scopedLastFortySo5Average,
                    }
                  : undefined
              }
            />
          </Vertical>
          <PlayerInjuryReport player={playerDetailsData.anyPlayer} />
          {!!playerDetailsData.anyPlayer.news && (
            <PlayerNewsUpdates player={playerDetailsData.anyPlayer} />
          )}
          <PlayerUnavailabilityPanel player={playerDetailsData.anyPlayer} />
          {!!withBuyingOptions && (
            <BuyingFloorPricePlayerDetails
              anyPlayer={playerDetailsData.anyPlayer}
              inSeason={withBuyingOptions.inSeason}
            />
          )}
          <Vertical gap={1.5}>
            <LazyFixtureChart
              playerSlug={playerDetailsData.anyPlayer.slug}
              onScoreSelect={onScoreSelect}
              disableLinks={onboarding}
              showUpsell={!onboarding}
              initialTeam={
                teamMode === 'CLUB'
                  ? playerDetailsData.anyPlayer.activeClub?.slug
                  : teamMode === 'NATIONAL_TEAM'
                    ? playerDetailsData.anyPlayer.activeNationalTeam?.slug
                    : undefined
              }
            />
          </Vertical>
          <PlayerUpcomingGames gamesWithTeam={upcomingGamesWithTeam} />
        </Content>
      </Main>
      {!!(
        !onboarding &&
        (withBuyingOptions || !watchlistId || !favoritesWatchlist)
      ) && (
        <Footer>
          {footer || (
            <>
              {((!watchlistId && !favoritesWatchlist) ||
                !!withBuyingOptions) && (
                <Horizontal>
                  {!watchlistId && !favoritesWatchlist && (
                    <AddToWatchlistButton
                      size="medium"
                      color="tertiary"
                      startIcon={<AddToWatchlistIcon />}
                      anyPlayer={playerDetailsData.anyPlayer}
                      fullWidth={!withBuyingOptions}
                    />
                  )}
                  {!!withBuyingOptions && (
                    <BuyingPlayerDetailsButton
                      anyPlayer={playerDetailsData.anyPlayer}
                      rarity={withBuyingOptions.rarity}
                      inSeason={withBuyingOptions.inSeason}
                      onMarketClick={withBuyingOptions.onMarketClick}
                    />
                  )}
                </Horizontal>
              )}
              {(!!watchlistId || !!favoritesWatchlist) && (
                <Horizontal>
                  <RemoveFromWatchlistButton
                    anyPlayer={playerDetailsData.anyPlayer}
                    watchlistId={watchlistId}
                    favoritesWatchlist={favoritesWatchlist}
                    onClose={onClose}
                  />
                  <AddToWatchlistButton
                    size="medium"
                    color="tertiary"
                    fullWidth
                    startIcon={<AddToWatchlistIcon />}
                    anyPlayer={playerDetailsData.anyPlayer}
                  />
                </Horizontal>
              )}
            </>
          )}
        </Footer>
      )}
    </Root>
  );
};

PlayerDetails.fragments = {
  playerGameScore: gql`
    fragment PlayerDetails_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      ...PlayerStats_playerGameScoreInterface
    }
    ${PlayerStats.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerDetails_playerGameScoreInterface>,
};
